import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Typography, Progress, Statistic, Select, 
  notification, Spin, Table, Tag
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  DollarOutlined, WarningOutlined,
  CheckCircleOutlined, PieChartOutlined, BarChartOutlined
} from "@ant-design/icons";
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

function BudgetDashboard() {
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const defaultFY = now.getMonth() >= 3
    ? `${now.getFullYear()}-${String(now.getFullYear()+1).slice(-2)}`
    : `${now.getFullYear()-1}-${String(now.getFullYear()).slice(-2)}`;
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(defaultFY);
  const [budgetSummary, setBudgetSummary] = useState([]);
  const [totalStats, setTotalStats] = useState({
    totalAllocated: 0,
    totalSpent: 0,
    totalRemaining: 0,
    averageUtilization: 0
  });

  const columns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PieChartOutlined style={{ color: '#1890ff' }} />
          <Text strong>{category?.replace('_', ' ')}</Text>
        </div>
      )
    },
    {
      title: 'Total Purposes',
      dataIndex: 'total_purposes',
      key: 'total_purposes',
      align: 'center',
      render: (count) => <Text>{count}</Text>
    },
    {
      title: 'Allocated Amount',
      dataIndex: 'total_allocated',
      key: 'total_allocated',
      render: (amount) => (
        <Text strong style={{ color: '#1890ff' }}>
          ₹{Number(amount).toLocaleString('en-IN')}
        </Text>
      )
    },
    {
      title: 'Spent Amount',
      dataIndex: 'total_spent',
      key: 'total_spent',
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          ₹{Number(amount).toLocaleString('en-IN')}
        </Text>
      )
    },
    {
      title: 'Remaining Amount',
      dataIndex: 'total_remaining',
      key: 'total_remaining',
      render: (amount) => (
        <Text strong style={{ color: '#fa8c16' }}>
          ₹{Number(amount).toLocaleString('en-IN')}
        </Text>
      )
    },
    {
      title: 'Utilization',
      dataIndex: 'avg_utilization',
      key: 'avg_utilization',
      render: (utilization, record) => {
        const percentage = parseFloat(utilization) || 0;
        let status = 'normal';
        let color = '#52c41a';
        
        if (percentage > 90) {
          status = 'exception';
          color = '#ff4d4f';
        } else if (percentage > 75) {
          status = 'active';
          color = '#fa8c16';
        }
        
        return (
          <div style={{ width: '120px' }}>
            <Progress 
              percent={percentage} 
              size="small" 
              status={status}
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </div>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const utilization = parseFloat(record.avg_utilization) || 0;
        if (utilization > 90) {
          return <Tag color="error" icon={<WarningOutlined />}>Over Budget</Tag>;
        } else if (utilization > 75) {
          return <Tag color="warning" >High Usage</Tag>;
        } else {
          return <Tag color="success" icon={<CheckCircleOutlined />}>On Track</Tag>;
        }
      }
    }
  ];

  useEffect(() => {
    fetchBudgetSummary();
  }, [selectedFiscalYear]);

  const fetchBudgetSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/budget-allocations/summary/${selectedFiscalYear}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.data.status) {
        const data = response.data.result;
        setBudgetSummary(data);
        
        // Calculate totals
        const totals = data.reduce((acc, item) => {
          acc.totalAllocated += parseFloat(item.total_allocated) || 0;
          acc.totalSpent += parseFloat(item.total_spent) || 0;
          acc.totalRemaining += parseFloat(item.total_remaining) || 0;
          return acc;
        }, { totalAllocated: 0, totalSpent: 0, totalRemaining: 0 });
        
        totals.averageUtilization = totals.totalAllocated > 0 
          ? (totals.totalSpent / totals.totalAllocated) * 100 
          : 0;
        
        setTotalStats(totals);
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch budget summary',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const getFiscalYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [];
    
    for (let i = -2; i <= 2; i++) {
      const year = currentYear + i;
      const fiscalYear = `${year}-${(year + 1).toString().slice(-2)}`;
      options.push(
        <Option key={fiscalYear} value={fiscalYear}>
          FY {fiscalYear}
        </Option>
      );
    }
    
    return options;
  };

  const getUtilizationColor = (percentage) => {
    if (percentage > 90) return '#ff4d4f';
    if (percentage > 75) return '#fa8c16';
    return '#52c41a';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <BarChartOutlined style={{ color: '#7a0000', fontSize: '24px' }} />
                <Title level={2} style={{ margin: 0, color: '#7a0000' }}>
                  Budget Dashboard
                </Title>
              </div>
              <Select
                value={selectedFiscalYear}
                onChange={setSelectedFiscalYear}
                style={{ width: 150 }}
                size="large"
              >
                {getFiscalYearOptions()}
              </Select>
            </div>

            {/* Summary Cards */}
            <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Allocated"
                    value={totalStats.totalAllocated}
                    prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
                    formatter={value => `₹${Number(value).toLocaleString('en-IN')}`}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Spent"
                    value={totalStats.totalSpent}
                    prefix={<span style={{ color: '#52c41a' }}>₹</span>}
                    formatter={value => `₹${Number(value).toLocaleString('en-IN')}`}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Remaining"
                    value={totalStats.totalRemaining}
                    prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
                    formatter={value => `₹${Number(value).toLocaleString('en-IN')}`}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: '14px' }}>Overall Utilization</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Progress
                        type="circle"
                        percent={Math.round(totalStats.averageUtilization)}
                        strokeColor={getUtilizationColor(totalStats.averageUtilization)}
                        size={80}
                      />
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Budget Summary Table */}
            <Card
              title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Budget Summary by Category - FY {selectedFiscalYear}</span>}
              headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            >
              <Table
                columns={columns}
                dataSource={budgetSummary.map((item, index) => ({
                  key: index,
                  ...item
                }))}
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: 'max-content' }}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                    <Table.Summary.Cell><Text strong>Total</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="center">
                      <Text strong>
                        {budgetSummary.reduce((sum, item) => sum + parseInt(item.total_purposes), 0)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong style={{ color: '#1890ff' }}>
                        ₹{totalStats.totalAllocated.toLocaleString('en-IN')}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong style={{ color: '#52c41a' }}>
                        ₹{totalStats.totalSpent.toLocaleString('en-IN')}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong style={{ color: '#fa8c16' }}>
                        ₹{totalStats.totalRemaining.toLocaleString('en-IN')}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Progress 
                        percent={Math.round(totalStats.averageUtilization)} 
                        size="small" 
                        strokeColor={getUtilizationColor(totalStats.averageUtilization)}
                        style={{ width: '120px' }}
                      />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      {totalStats.averageUtilization > 90 ? (
                        <Tag color="error" icon={<WarningOutlined />}>Critical</Tag>
                      ) : totalStats.averageUtilization > 75 ? (
                        <Tag color="warning" >Monitor</Tag>
                      ) : (
                        <Tag color="success" icon={<CheckCircleOutlined />}>Healthy</Tag>
                      )}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Card>

            {/* Key Insights */}
            <Row gutter={[24, 24]} style={{ marginTop: '32px' }}>
              <Col span={24}>
                <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Key Insights</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }} size="small">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <div style={{ textAlign: 'center', padding: '16px' }}>
                        <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                        <div style={{ marginTop: '8px' }}>
                          <Text strong style={{ color: '#52c41a' }}>
                            {budgetSummary.filter(item => parseFloat(item.avg_utilization) <= 75).length}
                          </Text>
                          <br />
                          <Text type="secondary">Categories On Track</Text>
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div style={{ textAlign: 'center', padding: '16px' }}>
                        {/* <TrendingUpOutlined style={{ fontSize: '24px', color: '#fa8c16' }} /> */}
                        <div style={{ marginTop: '8px' }}>
                          <Text strong style={{ color: '#fa8c16' }}>
                            {budgetSummary.filter(item => {
                              const util = parseFloat(item.avg_utilization);
                              return util > 75 && util <= 90;
                            }).length}
                          </Text>
                          <br />
                          <Text type="secondary">Categories Need Monitoring</Text>
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div style={{ textAlign: 'center', padding: '16px' }}>
                        <WarningOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
                        <div style={{ marginTop: '8px' }}>
                          <Text strong style={{ color: '#ff4d4f' }}>
                            {budgetSummary.filter(item => parseFloat(item.avg_utilization) > 90).length}
                          </Text>
                          <br />
                          <Text type="secondary">Categories Over Budget</Text>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default BudgetDashboard;